import logo from '@/assets/images/full-logo.webp';
import workspaceIcon from '@/assets/images/workspace.png';
import { IconFont, Spin } from '@/components';
import { QUERY_KEYS } from '@/constants';
import { getWorkspaceList } from '@/services/common';
import Icon from '@ant-design/icons';
import type { HeaderProps, MenuDataItem } from '@ant-design/pro-components';
import { Link, useInfiniteQuery, useLocation, useModel } from '@umijs/max';
import { Divider, Tooltip } from 'antd';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import CreateWorkSpaceModal from '../CreateWorkSpaceModal';
import {
  CustomCenter,
  FunctionCenter,
  MenuItem,
  TemplateCenter,
  Workbench,
} from './components';
import './index.less';
import useCustomFilterStore from '@/pages/CustomCenter/TemplateList/custom-center-filter';
import useTemplateFilterStore from '@/pages/TemplateCenter/template-center-filter';

const baseMenus = {
  '/workbench': Workbench,
  '/template-center': TemplateCenter,
  '/function-center': FunctionCenter,
  '/custom/template-list': CustomCenter,
} as Record<string, FC>;

export const Menu: FC<
  Parameters<NonUndefined<NonFalse<HeaderProps['menuRender']>>>[0]
> = (props) => {
  // props
  const { menuData } = props;
  // state
  const [createWorkSpaceModalVisible, setCreateWorkSpaceModalVisible] =
    useState(false);
  // ref
  const listRef = useRef<HTMLUListElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  // memo
  const [baseMenuData, hasWorkspacePermission] = useMemo(() => {
    if (menuData) {
      return menuData.reduce<[MenuDataItem[], boolean]>(
        (snapshot, menu) => {
          const path = menu.path!;

          if (baseMenus[path]) {
            snapshot[0].push(menu);
          }

          if (path === '/workspace') {
            snapshot[1] = true;
          }

          return snapshot;
        },
        [[], false],
      );
    }
    return [[], false] as [MenuDataItem[], boolean];
  }, [menuData]);
  // location
  const { pathname } = useLocation();
  // model
  const { initialState } = useModel('@@initialState');
  // query
  const {
    isLoading,
    hasNextPage,
    refetch,
    fetchNextPage,
    data: workspaceList,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.workspaceList, initialState?.userInfo.ou_uid],
    queryFn: ({ pageParam = 1, signal }) => {
      return getWorkspaceList(pageParam, signal);
    },
    getNextPageParam: (lastPage) => {
      const { current, pages } = lastPage.data;
      if (pages === 0 || current === pages) {
        return;
      }
      return current + 1;
    },
    enabled: hasWorkspacePermission && pathname !== SIGN_IN_PATH,
  });
  const hasMore = workspaceList && workspaceList.pages[0].data.pages > 1;
  useEffect(() => {
    if (!hasNextPage || cursorRef.current === null) {
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      },
      { root: listRef.current },
    );
    io.observe(cursorRef.current);
    return () => io.disconnect();
  }, [hasNextPage]);

  const { resetParams } = useCustomFilterStore();
  const { resetParams: resetParams2 } = useTemplateFilterStore();

  const resetAllParams = () => {
    resetParams();
    resetParams2();
  };

  return (
    <>
      <aside className="top-0 left-0 sticky flex flex-col flex-grow-0 flex-shrink-0 gap-3 h-screen bg-main-bg idp-menu basis-52">
        <section className="flex flex-grow-0 flex-shrink-0 justify-center items-center px-4 pt-4 basis-16">
          <Link
            to="/workbench"
            onClick={() => {
              resetAllParams();
            }}
          >
            <img className="h-10" loading="lazy" src={logo} alt="logo" />
          </Link>
        </section>
        <section className="flex flex-col flex-1 gap-2 h-0 font-medium text-main text-sm">
          <ul className="flex flex-col gap-2.5">
            {baseMenuData.map((route) => {
              const path = route.path!;
              const icon = route.icon as string;
              const name = route.name;

              if (route.source !== 'SERVER') return null;

              return (
                <MenuItem key={route.path} path={path}>
                  {({ isActive }) => (
                    <div onClick={resetAllParams}>
                      {isActive ? (
                        <Icon
                          className="mr-3 text-xl"
                          component={baseMenus[path]}
                        />
                      ) : (
                        <IconFont className="mr-3 text-xl" type={icon} />
                      )}
                      {name}
                    </div>
                  )}
                </MenuItem>
              );
            })}
          </ul>

          {hasWorkspacePermission && (
            <>
              <Divider className="w-auto min-w-0 h-auto mx-6 my-2 bg-[#E4E7EE]" />
              <section className="mx-3">
                <div className="flex justify-between items-center w-full h-10 px-3 rounded-md hover:bg-gradient-to-r hover:from-[#E3E3FF] hover:to-[#ECF2FF]">
                  <div className="inline-flex justify-center items-center font-medium text-main text-sm">
                    <IconFont
                      className="mr-3 text-xl"
                      type="icon-xiangmukongjian"
                    />
                    工作空间
                  </div>
                  <button
                    type="button"
                    className="inline-flex justify-center items-center hover:text-primary"
                    onClick={() => setCreateWorkSpaceModalVisible(true)}
                  >
                    <IconFont className="text-lg" type="icon-tianjiakongjian" />
                  </button>
                </div>
              </section>
              <ul
                className="flex flex-col flex-1 gap-1 overflow-y-auto nav-menu"
                ref={listRef}
              >
                {isLoading ? (
                  <div className="flex justify-center items-center gap-1.5 h-full">
                    <Spin width={16} height={16} />
                    <p className="text-secondary-3">加载中</p>
                  </div>
                ) : (
                  workspaceList &&
                  workspaceList.pages.map((page) =>
                    page.data.records.map((record) => {
                      const { workspace_id, workspace_uid, workspace_name } =
                        record;

                      return (
                        <MenuItem
                          secondary
                          key={workspace_uid}
                          path={{
                            pathname: `/empty/${workspace_id}/${workspace_uid}`,
                            search: `?code=4&id=${workspace_id}&uid=${workspace_uid}`,
                          }}
                        >
                          {({ isActive }) => (
                            <>
                              <img
                                className="w-4 mr-3"
                                src={workspaceIcon}
                                alt="workspace-icon"
                              />
                              <Tooltip
                                title={workspace_name}
                                mouseEnterDelay={1}
                              >
                                <span
                                  className={`flex-1 w-0 truncate ${
                                    isActive
                                      ? 'text-[13px] font-medium text-main'
                                      : 'text-xs text-secondary-1'
                                  }`}
                                >
                                  {workspace_name}
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </MenuItem>
                      );
                    }),
                  )
                )}
                {hasMore && (
                  <div
                    className="flex justify-center items-center gap-1"
                    ref={cursorRef}
                  >
                    <Spin width={16} height={16} />
                    <p className="text-secondary-2 text-xs">加载中</p>
                  </div>
                )}
              </ul>
            </>
          )}
        </section>
      </aside>
      <CreateWorkSpaceModal
        visible={createWorkSpaceModalVisible}
        onCancel={() => {
          setCreateWorkSpaceModalVisible(false);
        }}
        onOk={() => {
          refetch();
          setCreateWorkSpaceModalVisible(false);
        }}
      />
    </>
  );
};
